import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { ChevronDown, ChevronRight, History, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * "Who changed this, and when" (the auditability contract).
 *
 * Reads GET /api/settings/audit-log — the same audit_log rows every write
 * endpoint already records — and renders them as the owner's words:
 * "Restaurant Name → 'Vibez Cafe'", by name, with the time. Read-only by
 * nature; the refresh is explicit because history does not drift like a board.
 *
 * Given `keys` (the settings on the page being viewed), it shows only changes to
 * those, folded into one "History" line until opened: the same outlet-wide list of
 * Collect Feedback toggles under every page was history for show, not for the page
 * (floor report 2026-10-19).
 */
const ACTION_LABELS = {
  "setting.updated": "changed",
  "setting.bulk_updated": "changed",
  "setting.override_updated": "overrode",
  "setting.override_deleted": "cleared an override on",
  "config.updated": "changed",
};

const extractKeys = details => {
  const keys = [
    details?.new?.key,
    details?.old?.key,
    ...Object.keys(details?.new?.changes || {}),
  ];
  return [...new Set(keys.filter(Boolean))];
};

export default function SettingsAuditPanel({ token, metadataByKey = {}, keys = null }) {
  // The owner reads "Restaurant Name", not "restaurant_name" (DESIGN.md §7). A key the
  // registry no longer knows still shows, raw, rather than vanishing from history.
  const nameOf = key => metadataByKey[key]?.display_name || key;
  const [entries, setEntries] = useState(null);
  const [error, setError] = useState("");
  const [open, setOpen] = useState(false);

  const load = useCallback(() => {
    setError("");
    setEntries(null);
    axios
      .get(`${process.env.REACT_APP_BACKEND_URL || ""}/api/settings/audit-log?limit=${keys ? 200 : 20}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      })
      .then(({ data }) => setEntries(data))
      .catch(err => setError(err?.response?.data?.detail || err?.message || "Could not load history"));
  }, [token, keys ? 200 : 20]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const pageKeys = keys ? new Set(keys) : null;
  const shown = entries && (pageKeys
    ? entries.filter(entry => extractKeys(entry.details).some(key => pageKeys.has(key))).slice(0, 10)
    : entries);
  const Twisty = open ? ChevronDown : ChevronRight;

  // Page-scoped: one collapsible line, and nothing at all when the page has no history.
  if (pageKeys) {
    if (error) return <p className="px-3 py-2 text-meta text-danger" data-testid="settings-audit-error">Could not load this page's history: {error}</p>;
    if (!shown || shown.length === 0) return null;
  }

  return (
    <div className="rounded-md border bg-card" data-testid="settings-audit-panel">
      {pageKeys ? (
        <button
          type="button"
          className="w-full flex items-center gap-1.5 px-3 min-h-8 text-meta font-medium text-muted-foreground hover:bg-muted transition-colors"
          onClick={() => setOpen(next => !next)}
          aria-expanded={open}
          data-testid="settings-audit-toggle"
        >
          <Twisty className="w-3 h-3" aria-hidden="true" />
          <span>History of this page</span>
          <span className="ml-auto text-micro tabular-nums opacity-70">{shown.length}</span>
        </button>
      ) : (
      <div className="flex items-center justify-between gap-2 border-b px-3 py-2">
        <div>
          <h3 className="flex items-center gap-1.5 text-lede font-semibold">
            <History className="w-3.5 h-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />
            Recent changes
          </h3>
          <p className="text-meta text-muted-foreground">Who changed outlet settings, and when.</p>
        </div>
        <Button size="sm" variant="outline" className="h-8 px-2.5 text-body" onClick={load} data-testid="settings-audit-refresh">
          <RefreshCw className="w-3 h-3 mr-0.5" aria-hidden="true" />Refresh
        </Button>
      </div>
      )}

      {pageKeys && !open ? null : (<>
      {!entries && !error && (
        <p className="px-3 py-4 text-meta text-muted-foreground" data-testid="settings-audit-loading">Loading history…</p>
      )}
      {error && (
        <p className="px-3 py-4 text-meta text-danger" data-testid="settings-audit-error">{error}</p>
      )}
      {entries && entries.length === 0 && (
        <p className="px-3 py-4 text-meta text-muted-foreground" data-testid="settings-audit-empty">
          No recorded changes yet — the first save will appear here.
        </p>
      )}
      {shown && shown.length > 0 && (
        <ul className={`divide-y ${pageKeys ? "border-t" : ""}`} data-testid="settings-audit-list">
          {shown.map((entry, index) => {
            const keys = extractKeys(entry.details);
            return (
              <li key={index} className="px-3 py-1.5 text-meta" data-testid={`settings-audit-entry-${index}`}>
                <span className="font-semibold">{entry.by_name || entry.by_employee_id || "Unknown user"}</span>{" "}
                <span className="text-muted-foreground">{ACTION_LABELS[entry.action] || entry.action}</span>{" "}
                {entry.details?.new?.changes
                  ? Object.entries(entry.details.new.changes).map(([k, v], i) => (
                      <span key={k}>
                        {i > 0 && ", "}
                        <span>{nameOf(k)}</span>
                        <span className="text-muted-foreground"> → “{String(v)}”</span>
                      </span>
                    ))
                  : <span>{keys.map(nameOf).join(", ")}</span>}
                {typeof entry.details?.new?.value === "string" && !Array.isArray(entry.details?.new?.changes) && (
                  <span className="text-muted-foreground"> → “{entry.details.new.value}”</span>
                )}
                <span className="ml-1 tabular-nums text-muted-foreground">· {formatWhen(entry.at)}</span>
              </li>
            );
          })}
        </ul>
      )}
      </>)}
    </div>
  );
}

/** "2 min ago" style stamp; a raw timestamp beats a wrong relative label. */
function formatWhen(iso) {
  if (!iso) return "";
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return iso;
  const seconds = Math.max(0, Math.round((Date.now() - then.getTime()) / 1000));
  if (seconds < 60) return `${seconds}s ago`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)} min ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)} h ago`;
  return then.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}
